import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from '@material-ui/core';
import { useIntl } from 'react-intl';
import { StepButtonBar } from '../OtherRequirementWizard/StepButtonBar';
import { MTable } from '../../../../../Components';
import { useAptorApi } from '../../../../../Api';
import { IGridQueryRequest } from '../../../../../Api/AptorApi';
import UpdateConnectionInfo from './UpdateConnectionInfo';

const PRIMARY = '#c4122f';

interface DisconnectStep1CompanyUnitsTableProps {
  selectedRequirement: { id: number; name: string } | null;
  selectedCompanyUnits: any[];
  onCompanyUnitsSelect: (units: any[]) => void;
  onNext: () => void;
  onBack: () => void;
  onCancel: () => void;
  formatMessage: (descriptor: any) => string;
  currentStepName?: string;
  updateConnectionInfo?: any;
  setUpdateConnectionInfo?: (info: any) => void;
}

const DisconnectStep1CompanyUnitsTable: React.FC<DisconnectStep1CompanyUnitsTableProps> = ({
  selectedRequirement,
  selectedCompanyUnits,
  onCompanyUnitsSelect,
  onNext,
  onBack,
  onCancel,
  formatMessage,
  currentStepName = 'disconnect-select',
  updateConnectionInfo,
  setUpdateConnectionInfo,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { api } = useAptorApi();
  const [loading, setLoading] = useState(false);
  const [companyUnits, setCompanyUnits] = useState<any[]>([]);
  const tableRef = useRef<any>(null);
  const paperPadding = isMobile ? theme.spacing(3) : theme.spacing(4);

  useEffect(() => {
    const fetchCompanyUnits = async () => {
      if (!selectedRequirement?.id) return;
      setLoading(true);
      try {
        const request: IGridQueryRequest = {
          page: 0,
          pageSize: 1000,
          orderBy: 'name',
          orderDirection: 0,
          search: '',
        };
        const result = await api.searchRequirementCompanyUnits(selectedRequirement.id, request);
        setCompanyUnits(result?.data || []);
      } catch (error) {
        console.error('Error fetching company units:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCompanyUnits();
  }, [selectedRequirement?.id, api]);

  const handleToggle = useCallback((unit: any) => {
    const isSelected = selectedCompanyUnits.some((u) => u.id === unit.id);
    if (isSelected) {
      onCompanyUnitsSelect(selectedCompanyUnits.filter((u) => u.id !== unit.id));
    } else {
      onCompanyUnitsSelect([...selectedCompanyUnits, unit]);
    }
  }, [selectedCompanyUnits, onCompanyUnitsSelect]);

  const checkboxAction = useCallback((row: any) => {
    const isSelected = selectedCompanyUnits.some((unit) => unit.id === row.id);
    return {
      tooltip: isSelected
        ? formatMessage({ id: 'component.mtable.unselectTooltip' })
        : formatMessage({ id: 'component.mtable.selectTooltip' }),
      icon: isSelected ? 'check_box' : 'check_box_outline_blank',
      isFreeAction: true,
      onClick: (event: any) => {
        event.stopPropagation();
        handleToggle(row);
      },
      iconProps: {
        style: { color: '#c4122f' },
      },
    };
  }, [selectedCompanyUnits, handleToggle, formatMessage]);

  const isValid = selectedCompanyUnits.length > 0;

  return (
    <div style={{ width: '100%' }}>
      <div
        style={{
          display: 'flex',
          width: '100%',
          gap: '10px',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'stretch',
        }}
      >
        <div
          style={{
            width: isMobile ? '100%' : 'calc(50% - 5px)',
            flexShrink: 0,
            padding: paperPadding,
            backgroundColor: '#f7f0f1',
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
            minHeight: '350px',
          }}
        >
          <Box style={{ width: '100%' }}>

            {loading ? (
              <Box style={{ display: 'flex', justifyContent: 'center', padding: theme.spacing(4) }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box style={{ maxHeight: '500px', overflow: 'auto', marginTop: theme.spacing(2) }}>
                <MTable
                  key={`disconnect-table-${selectedCompanyUnits.length}`}
                  tableRef={tableRef}
                  columns={[{ title: formatMessage({ id: 'form.name' }), field: 'name' }]}
                  actions={[checkboxAction]}
                  data={companyUnits}
                  components={{}}
                />
              </Box>
            )}
          </Box>
        </div>
        {updateConnectionInfo !== undefined && setUpdateConnectionInfo !== undefined && (
          <UpdateConnectionInfo
            updateConnectionInfo={updateConnectionInfo}
            currentStepName={currentStepName}
            onUpdate={setUpdateConnectionInfo}
          />
        )}
      </div>

      <Box>
        <StepButtonBar
          onCancel={onCancel}
          onBack={onBack}
          onNext={onNext}
          nextLabel="form.next"
          nextDisabled={!isValid}
          formatMessage={formatMessage}
          showBack={true}
        />
      </Box>
    </div>
  );
};

export default DisconnectStep1CompanyUnitsTable;
